/** The weekly members' package, the title's Weekly package tab: each upcoming week's two pieces, its status, and the results so far. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelPackage, createPackage, listPackages, listPieces, packageGaps, packageHistory, previewPackage, releasePackage, updatePackage,
  type PackageNotLiveLink, type PackageSlot, type PieceRow, type UsedPiece, type WeeklyPackage,
} from '../api'
import { canPublish, useEditorialMe } from '../me'
import { useEditorialTitle } from '../titleContext'
import { describeError } from '../../lib/describeError'
import {
  AWAITING_APPROVAL, canChange, pickable, canRelease, easternToday, easternToIso, gapWarning, holdNote, numbersText, packageChip, pickerPieces, releaseMessage, ROLE_DESTINATION, sendWhen,
  switchBanner, TONE_CLASS, visibleUpcoming, type PickerOption, type Role,
} from './weekly'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'rounded-xl border border-ink-500 bg-ink-700'
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`
const field = `box-border min-h-10 w-full min-w-0 rounded-lg border border-ink-400 bg-ink-800 px-3 py-2 font-ui text-[14px] text-paper disabled:opacity-60 ${focus}`
const link = `text-[13px] text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`
const CHIP = 'inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold whitespace-nowrap'

const ROLE_LABEL: Record<Role, string> = { full: 'Full article (members only)', short: 'Short version (free section)' }

type Option = PickerOption

/** Email widths for the preview: a desktop mail client's reading pane and a phone. */
const VIEW_WIDTH = { desktop: 680, phone: 390 } as const
type View = keyof typeof VIEW_WIDTH

/** One email at its real width, as tall as its content so the page scrolls instead of the frame.
 *  The frame may run no scripts and open nothing; same-origin only lets the page read its height
 *  and catch link clicks, which open here in a new tab when they are plain web links. A link to a
 *  piece not on the site yet is outlined and opens nothing: its page only exists once the send publishes it. */
function EmailFrame({ title, html, view, notLive, onNotLive }: {
  title: string; html: string; view: View; notLive: string[]; onNotLive: () => void
}) {
  const [height, setHeight] = useState(700)
  const onLoad = (ev: React.SyntheticEvent<HTMLIFrameElement>) => {
    const d = ev.currentTarget.contentDocument
    if (!d) return
    // Compared without query or fragment, so a tracking tag on the link doesn't hide it.
    const bare = (u: string) => u.split(/[?#]/)[0]
    const pending = (a: Element) => notLive.map(bare).includes(bare((a as HTMLAnchorElement).href))
    d.querySelectorAll('a[href]').forEach((a) => {
      if (!pending(a)) return
      const el = a as HTMLElement
      el.style.outline = '2px dashed #d97706'
      el.style.outlineOffset = '2px'
      el.title = 'Not live yet: this page is published at the send'
    })
    setHeight(d.documentElement.scrollHeight + 4)
    d.addEventListener('click', (e) => {
      const a = (e.target as Element | null)?.closest?.('a[href]')
      if (!a) return
      e.preventDefault()
      if (pending(a)) { onNotLive(); return }
      const href = (a as HTMLAnchorElement).href
      if (/^https?:\/\//i.test(href)) window.open(href, '_blank', 'noopener,noreferrer')
    })
  }
  return (
    <iframe key={view} title={title} srcDoc={html} sandbox="allow-same-origin" onLoad={onLoad}
      style={{ maxWidth: VIEW_WIDTH[view], height }}
      className="mx-auto block w-full rounded-md border border-ink-500 bg-white shadow-lg" />
  )
}

/** The note over the email naming the links that open nothing yet; it lights up when one is clicked. */
function NotLiveNote({ links, flash }: { links: PackageNotLiveLink[]; flash: boolean }) {
  if (links.length === 0) return null
  return (
    <div role="status" className={`flex flex-col gap-1 rounded-lg border p-3 text-[13px] ${flash ? 'border-amber-glow bg-ink-600' : 'border-ink-500 bg-ink-700'}`}>
      <strong className="text-amber-glow">Not live yet (dashed outline)</strong>
      {links.map((l) => (
        <span key={l.role} className="text-paper-dim">
          {l.label} is not on the site yet, so its link opens a missing page until the send publishes it:{' '}
          <span className="break-all text-paper">{l.url}</span>
        </span>
      ))}
    </div>
  )
}

const about = (n: number | null) => (n === null ? '' : `about ${n.toLocaleString('en-US')}`)

/** The week's three emails as they would go out with the pieces picked, at the send time:
 *  a list of the three on the left, the chosen one on the right as a reader's inbox shows it. */
function EmailPreview({ slug, sendAt, fullId, shortId }: { slug: string; sendAt: string; fullId: number; shortId: number }) {
  const q = useQuery({
    queryKey: ['editorial', 'packages', slug, 'preview', sendAt, fullId, shortId],
    queryFn: () => previewPackage(slug, sendAt, fullId, shortId), retry: false,
  })
  const [tab, setTab] = useState(0)
  const [view, setView] = useState<View>('desktop')
  const [flash, setFlash] = useState(false)
  if (q.isPending) return <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">Building the emails…</p>
  if (q.isError || !q.data) return <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">Could not build the emails. {describeError(q.error)}</p>
  const p = q.data
  const e = p.emails[Math.min(tab, p.emails.length - 1)]
  const counts = p.emails.map((m) => m.count)
  const total = counts.every((c) => c !== null) ? counts.reduce<number>((a, c) => a + (c ?? 0), 0) : null
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-ink-500 bg-ink-800 p-3 sm:p-4">
      <p className="m-0 font-ui text-[14px] text-paper">
        {p.emails.length} emails go out {sendWhen(p.send_at)}{total !== null ? `, to ${about(total)} readers in all` : ''}.
      </p>
      {p.draft && <p className="m-0 font-ui text-[13px] font-semibold text-amber-glow">Draft: pieces not yet approved. The emails will change if the pieces do.</p>}
      {p.problems.length > 0 && (
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 font-ui text-[13px] text-amber-glow">{p.problems.map((x) => <li key={x}>{x}</li>)}</ul>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(220px,280px)_1fr]">
        <div role="tablist" aria-label="Emails" aria-orientation="vertical" className="flex flex-col gap-2">
          {p.emails.map((m, i) => (
            <button key={m.key} type="button" role="tab" aria-selected={i === tab} onClick={() => setTab(i)}
              className={`flex flex-col gap-1 rounded-lg border p-3 text-left font-ui ${focus} ${i === tab ? 'border-amber-glow bg-ink-700' : 'border-ink-500 hover:bg-ink-700'}`}>
              <span className={`text-[13px] font-semibold ${i === tab ? 'text-amber-glow' : 'text-paper'}`}>{m.label}</span>
              <span className="text-[12px] text-paper-dim">{m.audience}{m.count !== null ? ` · ${about(m.count)}` : ''}</span>
              {m.error
                ? <span className="text-[12px] font-semibold text-radar-red">Could not be made</span>
                : <span className="line-clamp-2 text-[12px] text-paper-faint">{m.subject}</span>}
            </button>
          ))}
        </div>
        {e && (
          <div role="tabpanel" aria-label={e.label} className="flex min-w-0 flex-col gap-3 font-ui">
            <div className="flex flex-col gap-1 rounded-lg border border-ink-500 bg-ink-700 p-3 text-[13px]">
              <span className="text-paper-dim">From <span className="text-paper">{e.from ?? '—'}</span></span>
              <span className="text-paper-dim">To <span className="text-paper">{e.audience}{e.count !== null ? ` (${about(e.count)})` : ''}</span></span>
              <strong className="mt-1 text-[16px] leading-snug text-paper">{e.subject || 'No subject'}</strong>
              {e.preheader && <span className="text-paper-dim">{e.preheader}</span>}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12px] text-paper-faint">Shown for a sample member. Real emails carry each reader&rsquo;s name and sign-in links.</span>
              <div role="group" aria-label="Width" className="flex gap-1">
                {(Object.keys(VIEW_WIDTH) as View[]).map((v) => (
                  <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
                    className={`${btn} min-h-8 px-3 text-[12px] ${view === v ? 'border-amber-glow text-amber-glow' : ''}`}>{v === 'desktop' ? 'Desktop' : 'Phone'}</button>
                ))}
              </div>
            </div>
            {e.error
              ? <p role="alert" className="m-0 text-[13px] text-radar-red">{e.error}</p>
              : <>
                  <NotLiveNote links={p.not_live ?? []} flash={flash} />
                  <div className="rounded-lg bg-ink-900 p-2 sm:p-4">
                    <EmailFrame key={e.key} title={`${e.label} preview`} html={e.html} view={view} notLive={(p.not_live ?? []).map((l) => l.url)}
                      onNotLive={() => { setFlash(true); window.setTimeout(() => setFlash(false), 1500) }} />
                  </div>
                </>}
          </div>
        )}
      </div>
    </div>
  )
}

function PreviewToggle({ slug, sendAt, fullId, shortId, open, setOpen }: {
  slug: string; sendAt: string; fullId: string; shortId: string; open: boolean; setOpen: (o: boolean) => void
}) {
  if (!fullId || !shortId || !sendAt) return null
  return (
    <div className="flex flex-col gap-2">
      <div><button type="button" className={btn} aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide the emails' : 'Preview emails'}</button></div>
      {open && <EmailPreview slug={slug} sendAt={sendAt} fullId={Number(fullId)} shortId={Number(shortId)} />}
    </div>
  )
}

/** Admins only: the campaign pages live in the admin console, which editors and writers cannot open. */
function Campaigns({ pkg }: { pkg: WeeklyPackage }) {
  if (pkg.members_campaign_id === null && pkg.join_campaign_id === null) return null
  return (
    <span className="flex flex-wrap gap-x-4 gap-y-1">
      {pkg.members_campaign_id !== null && <Link to={`/campaigns/${pkg.members_campaign_id}/results`} className={link}>Members&rsquo; email results</Link>}
      {pkg.join_campaign_id !== null && <Link to={`/campaigns/${pkg.join_campaign_id}/results`} className={link}>Join email results</Link>}
    </span>
  )
}

function Pick({ role, id, value, options, disabled, onChange, current, packageId }: {
  role: Role; id: string; value: string; options: Option[]; disabled: boolean; onChange: (v: string) => void; current: WeeklyPackage['full'] | null; packageId: number | null
}) {
  const opts = current && !options.some((o) => o.id === current.id)
    ? [{ id: current.id, title: current.title ?? `Piece ${current.id}`, approved: current.status === 'approved' || current.status === 'published' }, ...options] : options
  const chosen = opts.find((o) => String(o.id) === value)
  const gaps = useQuery({
    queryKey: ['editorial', 'packages', 'gaps', role, value],
    queryFn: () => packageGaps(Number(value), role),
    enabled: value !== '', refetchInterval: 60_000,
  })
  const warning = value !== '' ? gapWarning(gaps.data?.gaps) : null
  return (
    <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-1.5">
      <label htmlFor={id} className="font-ui text-[12px] font-semibold text-paper-dim">{ROLE_LABEL[role]}</label>
      <select id={id} className={field} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">Pick a piece…</option>
        {opts.map((o) => pickable(o, packageId)
          ? <option key={o.id} value={o.id}>{o.title}{o.approved ? '' : ` (${AWAITING_APPROVAL})`}</option>
          : <option key={o.id} value={o.id} disabled className="text-paper-faint">{o.title} &mdash; {o.used?.note}</option>)}
      </select>
      {chosen && !chosen.approved && <span className="font-ui text-[12px] text-amber-glow">{chosen.title} is {AWAITING_APPROVAL}.</span>}
      {warning && <span role="alert" className="font-ui text-[12px] font-semibold text-amber-glow">{warning}</span>}
    </div>
  )
}

function ReadOnlyPiece({ role, title, gaps }: { role: Role; title: string | null; gaps?: string[] }) {
  const warning = gapWarning(gaps)
  return (
    <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-1">
      <span className="font-ui text-[12px] font-semibold text-paper-dim">{ROLE_LABEL[role]}</span>
      <span className="font-ui text-[14px] text-paper">{title ?? 'Not picked yet'}</span>
      {warning && <span className="font-ui text-[12px] font-semibold text-amber-glow">{warning}</span>}
    </div>
  )
}

function Slot({ slot, editor, admin, on, options, slug, now }: { slot: PackageSlot; editor: boolean; admin: boolean; on: boolean; options: Record<Role, Option[]>; slug: string; now: Date }) {
  const qc = useQueryClient()
  const pkg = slot.package
  const [full, setFull] = useState<string | null>(null)
  const [short, setShort] = useState<string | null>(null)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [preview, setPreview] = useState(false)
  const fullV = full ?? String(pkg?.full.id ?? '')
  const shortV = short ?? String(pkg?.short.id ?? '')
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['editorial', 'packages', slug] }) }
  const ok = (text: string) => { setMsg({ ok: true, text }); setFull(null); setShort(null); refresh() }
  const fail = (e: unknown) => { setMsg({ ok: false, text: describeError(e) }); refresh() }
  const save = useMutation({
    mutationFn: () => pkg
      ? updatePackage(pkg.id, { full_draft_id: Number(fullV), short_draft_id: Number(shortV) })
      : createPackage({ publication_slug: slug, send_at: slot.send_at, full_draft_id: Number(fullV), short_draft_id: Number(shortV) }),
    onSuccess: () => ok('Saved.'), onError: fail,
  })
  const cancel = useMutation({ mutationFn: () => cancelPackage(pkg!.id), onSuccess: () => ok('Cancelled.'), onError: fail })
  const release = useMutation({ mutationFn: () => releasePackage(pkg!.id), onSuccess: (r) => ok(releaseMessage(r.status)), onError: fail })
  const busy = save.isPending || cancel.isPending || release.isPending
  const chip = pkg ? packageChip(pkg, on) : null
  const note = pkg ? holdNote(pkg) : null
  const changeable = canChange(pkg, editor)
  const dirty = fullV !== String(pkg?.full.id ?? '') || shortV !== String(pkg?.short.id ?? '')
  return (
    <article className={`${card} flex min-w-0 flex-col gap-3 p-[18px] ${preview ? 'lg:col-span-2' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 font-serif text-[20px] font-medium leading-tight text-paper">{sendWhen(slot.send_at)}</h3>
        {chip ? <span className={`${CHIP} ${TONE_CLASS[chip.tone]}`}>{chip.label}</span> : <span className={`${CHIP} ${TONE_CLASS.plain}`}>Nothing planned</span>}
      </div>
      <div className="flex flex-wrap gap-4">
        {changeable ? (
          <>
            <Pick role="full" id={`wk-full-${slot.send_at}`} value={fullV} options={options.full} disabled={busy} onChange={setFull} current={pkg?.full ?? null} packageId={pkg?.id ?? null} />
            <Pick role="short" id={`wk-short-${slot.send_at}`} value={shortV} options={options.short} disabled={busy} onChange={setShort} current={pkg?.short ?? null} packageId={pkg?.id ?? null} />
          </>
        ) : (
          <>
            <ReadOnlyPiece role="full" title={pkg?.full.title ?? null} gaps={pkg?.status === 'planned' || pkg?.status === 'complete' ? pkg.full.gaps : undefined} />
            <ReadOnlyPiece role="short" title={pkg?.short.title ?? null} gaps={pkg?.status === 'planned' || pkg?.status === 'complete' ? pkg.short.gaps : undefined} />
          </>
        )}
      </div>
      {pkg && pkg.reasons.length > 0 && (
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 font-ui text-[13px] text-paper-dim">
          {pkg.reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      {note && <p className="m-0 font-ui text-[13px] font-semibold text-amber-glow">{note}</p>}
      {pkg && admin && <Campaigns pkg={pkg} />}
      {pkg?.status !== 'sent' && pkg?.status !== 'staff_sent' && <PreviewToggle slug={slug} sendAt={slot.send_at} fullId={fullV} shortId={shortV} open={preview} setOpen={setPreview} />}
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`m-0 font-ui text-[13px] ${msg.ok ? 'text-radar-green' : 'text-radar-red'}`}>{msg.text}</p>}
      {(changeable || (pkg && canRelease(pkg, now, editor))) && (
        <div className="flex flex-wrap gap-2">
          {changeable && (
            <button type="button" className={btnPrimary} disabled={busy || !fullV || !shortV || !dirty} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : pkg ? 'Change the pair' : 'Plan this week'}
            </button>
          )}
          {pkg && changeable && (
            <button type="button" className={btn} disabled={busy}
              onClick={() => { if (window.confirm(`Cancel the package for ${sendWhen(slot.send_at)}? Nothing will be sent that week.`)) cancel.mutate() }}>
              {cancel.isPending ? 'Cancelling…' : 'Cancel this week'}
            </button>
          )}
          {pkg && canRelease(pkg, now, editor) && (
            <button type="button" className={btnPrimary} disabled={busy}
              onClick={() => { if (window.confirm('Release this package now? Both emails send at once.')) release.mutate() }}>
              {release.isPending ? 'Releasing…' : 'Release now'}
            </button>
          )}
        </div>
      )}
      {pkg && changeable && !dirty && !busy && (
        <span className="font-ui text-[12px] text-paper-faint">To change the pair, pick a different piece above.</span>
      )}
    </article>
  )
}

/** Editors: a one-off send at any future time today or later, outside the title's day and 10:00
 *  (one package per title per day). The members' article may already be live. */
function ExtraSend({ slug, options }: { slug: string; options: Record<Role, Option[]> }) {
  const qc = useQueryClient()
  const [day, setDay] = useState(() => easternToday(new Date()))
  const [time, setTime] = useState('14:00')
  const [full, setFull] = useState('')
  const [short, setShort] = useState('')
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [preview, setPreview] = useState(false)
  const plan = useMutation({
    mutationFn: () => createPackage({ publication_slug: slug, send_at: easternToIso(day, time), full_draft_id: Number(full), short_draft_id: Number(short), extra: true }),
    onSuccess: () => { setMsg({ ok: true, text: 'Planned. It shows above with its own time.' }); setFull(''); setShort(''); void qc.invalidateQueries({ queryKey: ['editorial', 'packages', slug] }) },
    onError: (e) => setMsg({ ok: false, text: describeError(e) }),
  })
  const when = day && time ? sendWhen(easternToIso(day, time)) : ''
  return (
    <section aria-labelledby="wk-extra" className={`${card} flex flex-col gap-3 p-[18px]`}>
      <h3 id="wk-extra" className="label m-0">Extra send</h3>
      <p className="m-0 font-ui text-[13px] text-paper-dim">
        A one-off send outside the usual day and time, for example a week that missed its day. Only one package can go out per day.
      </p>
      <div className="flex flex-wrap gap-4">
        <div className="flex min-w-0 flex-[0_1_180px] flex-col gap-1.5">
          <label htmlFor="wk-extra-day" className="font-ui text-[12px] font-semibold text-paper-dim">Day</label>
          <input id="wk-extra-day" type="date" className={field} value={day} min={easternToday(new Date())} disabled={plan.isPending} onChange={(e) => setDay(e.target.value)} />
        </div>
        <div className="flex min-w-0 flex-[0_1_140px] flex-col gap-1.5">
          <label htmlFor="wk-extra-time" className="font-ui text-[12px] font-semibold text-paper-dim">Time (Eastern)</label>
          <input id="wk-extra-time" type="time" step={300} className={field} value={time} disabled={plan.isPending} onChange={(e) => setTime(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-wrap gap-4">
        <Pick role="full" id="wk-extra-full" value={full} options={options.full} disabled={plan.isPending} onChange={setFull} current={null} packageId={null} />
        <Pick role="short" id="wk-extra-short" value={short} options={options.short} disabled={plan.isPending} onChange={setShort} current={null} packageId={null} />
      </div>
      {day && time && <PreviewToggle slug={slug} sendAt={easternToIso(day, time)} fullId={full} shortId={short} open={preview} setOpen={setPreview} />}
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`m-0 font-ui text-[13px] ${msg.ok ? 'text-radar-green' : 'text-radar-red'}`}>{msg.text}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnPrimary} disabled={plan.isPending || !day || !time || !full || !short}
          onClick={() => { if (window.confirm(`Plan an extra send for ${when}? Both emails go out then once both pieces are approved.`)) plan.mutate() }}>
          {plan.isPending ? 'Planning…' : 'Plan extra send'}
        </button>
      </div>
    </section>
  )
}

function History({ slug }: { slug: string }) {
  const q = useQuery({ queryKey: ['editorial', 'packages', slug, 'history'], queryFn: () => packageHistory(slug) })
  const rows = q.data?.history ?? []
  return (
    <section aria-labelledby="wk-history" className={`${card} flex flex-col gap-3 p-[18px]`}>
      <h3 id="wk-history" className="label m-0">Past weeks</h3>
      {q.isPending && <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">Could not load the past weeks. {describeError(q.error)}</p>}
      {q.data && rows.length === 0 && <p className="m-0 font-ui text-[13px] text-paper-dim">Nothing has gone out yet.</p>}
      {rows.map((r) => {
        const chip = packageChip(r)
        return (
          <div key={r.id} className="flex flex-col gap-1.5 border-t border-ink-500 pt-2.5 font-ui">
            <div className="flex flex-wrap items-center gap-2">
              <strong className="text-[13px] text-paper">{sendWhen(r.send_at)}</strong>
              <span className={`${CHIP} ${TONE_CLASS[chip.tone]}`}>{chip.label}</span>
            </div>
            <span className="text-[13px] text-paper">{r.full_title ?? 'Full article'} · {r.short_title ?? 'Short version'}</span>
            <span className="text-[12px] text-paper-faint">Members: {numbersText(r.members)}</span>
            <span className="text-[12px] text-paper-faint">Join: {numbersText(r.join)}, {r.join.joins.toLocaleString('en-US')} joined</span>
          </div>
        )
      })}
    </section>
  )
}

function usePickable(slug: string, enabled: boolean, used: UsedPiece[]): Record<Role, Option[]> {
  const get = (role: Role, stage: 'to_review' | 'approved') => ({
    queryKey: ['editorial', 'packages', slug, 'pieces', role, stage],
    queryFn: () => listPieces(slug, { stage, destination: ROLE_DESTINATION[role], limit: 100 }),
    enabled,
  })
  const fr = useQuery(get('full', 'to_review')); const fa = useQuery(get('full', 'approved'))
  const live = (role: Role) => ({
    queryKey: ['editorial', 'packages', slug, 'pieces', role, 'live'],
    queryFn: () => listPieces(slug, { stage: 'live', destination: ROLE_DESTINATION[role], limit: 100 }),
    enabled,
  })
  const fl = useQuery(live('full'))
  const sr = useQuery(get('short', 'to_review')); const sa = useQuery(get('short', 'approved')); const sl = useQuery(live('short'))
  const rows = (...qs: (typeof fr)[]): Pick<PieceRow, 'id' | 'title' | 'destination' | 'stage'>[] => qs.flatMap((q) => q.data?.items ?? [])
  return { full: pickerPieces(rows(fr, fa, fl), 'full', true, used), short: pickerPieces(rows(sr, sa, sl), 'short', true, used) }
}

export function WeeklyPanel() {
  const { publicationSlug: slug } = useEditorialTitle()
  const me = useEditorialMe()
  const editor = canPublish(me.data)
  const admin = me.data?.is_admin === true
  const q = useQuery({ queryKey: ['editorial', 'packages', slug], queryFn: () => listPackages(slug), refetchInterval: 60_000 })
  const options = usePickable(slug, editor, q.data?.used ?? [])
  if (q.isPending) return <p role="status" className="font-ui text-[14px] text-paper-dim">Loading the weekly package…</p>
  if (q.isError || !q.data) return <p role="alert" className="font-ui text-[14px] text-radar-red">Could not load the weekly package. {describeError(q.error)}</p>
  const slots = visibleUpcoming(q.data.upcoming)
  const now = new Date()
  const banner = switchBanner(q.data.weekly_package_on, q.data.weekly_package_live)
  return (
    <section aria-labelledby="wk-title" className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 id="wk-title" className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[40px]">Weekly members&rsquo; package</h1>
        <Link to="/editorial/help#weekly" className={link}>How it works</Link>
      </div>
      {banner && <p role="status" className="m-0 rounded-lg bg-amber-glow/20 px-3 py-2 font-ui text-[13px] font-semibold text-amber-glow">{banner}</p>}
      {!editor && me.data && <p className="m-0 font-ui text-[13px] text-paper-dim">Editors pair each week&rsquo;s pieces. You can see where each week stands.</p>}
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        {slots.map((s) => <Slot key={s.send_at} slot={s} editor={editor} admin={admin} on={q.data.weekly_package_on} options={options} slug={slug} now={now} />)}
      </div>
      {editor && q.data.weekly_package_on && <ExtraSend slug={slug} options={options} />}
      <History slug={slug} />
    </section>
  )
}
